import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ThumbsUp, Handshake, Target, Smile } from 'lucide-react';
import { toast } from 'sonner';

const PlayerEndorsements = ({ userId, className = '' }) => {
  const { API, user } = useContext(AuthContext);
  const [endorsements, setEndorsements] = useState(null);
  const [myEndorsements, setMyEndorsements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      fetchEndorsements();
      if (user && user.id !== userId) {
        checkMyEndorsements();
      }
    }
  }, [userId, user]);

  const fetchEndorsements = async () => {
    try {
      const response = await axios.get(`${API}/endorsements/${userId}`);
      setEndorsements(response.data);
    } catch (error) {
      console.error('Failed to fetch endorsements:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkMyEndorsements = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/endorsements/${userId}/check/${user.id}`);
      setMyEndorsements(response.data.endorsement_types || []);
    } catch (error) {
      console.error('Failed to check endorsements:', error);
    }
  };

  const handleEndorse = async (type) => {
    if (!user) {
      toast.error('עליך להיות מחובר כדי להמליץ');
      return;
    }

    if (user.id === userId) {
      toast.error('לא ניתן להמליץ על עצמך');
      return;
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.post(
        `${API}/endorsements`,
        {
          target_user_id: userId,
          endorsement_type: type
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.action === 'added') {
        setMyEndorsements([...myEndorsements, type]);
        toast.success('ההמלצה נוספה!');
      } else {
        setMyEndorsements(myEndorsements.filter(e => e !== type));
        toast.info('ההמלצה הוסרה');
      }

      // רענון
      fetchEndorsements();
    } catch (error) {
      console.error('Failed to endorse:', error);
      toast.error('שגיאה בהוספת המלצה');
    }
  };

  const endorsementTypes = [
    {
      type: 'strong_player',
      icon: ThumbsUp,
      label: 'שחקן חזק',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      activeColor: 'bg-red-600'
    },
    {
      type: 'fair_player',
      icon: Handshake,
      label: 'שחקן הוגן',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      activeColor: 'bg-blue-600'
    },
    {
      type: 'great_technique',
      icon: Target,
      label: 'טכניקה מעולה',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      activeColor: 'bg-green-600'
    },
    {
      type: 'pleasant_person',
      icon: Smile,
      label: 'אדם נעים',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      activeColor: 'bg-yellow-600'
    }
  ];

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>המלצות</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-gray-500">טוען...</div>
        </CardContent>
      </Card>
    );
  }

  if (!endorsements) {
    return null;
  }

  const isMyProfile = user && user.id === userId;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ThumbsUp className="w-5 h-5" />
          המלצות מהקהילה
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {endorsementTypes.map(({ type, icon: Icon, label, color, bgColor, borderColor, activeColor }) => {
            const count = endorsements.counts[type] || 0;
            const isEndorsed = myEndorsements.includes(type);

            return (
              <button
                key={type}
                onClick={() => !isMyProfile && handleEndorse(type)}
                disabled={isMyProfile}
                className={`p-4 rounded-lg border-2 transition-all ${
                  isEndorsed 
                    ? `${activeColor} text-white border-transparent` 
                    : `${bgColor} ${borderColor} hover:scale-105`
                } ${isMyProfile ? 'cursor-default' : 'cursor-pointer'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${isEndorsed ? 'text-white' : color}`} />
                    <span className={`text-sm font-medium ${isEndorsed ? 'text-white' : 'text-gray-700'}`}>
                      {label}
                    </span>
                  </div>
                  <span className={`text-2xl font-bold ${isEndorsed ? 'text-white' : color}`}>
                    {count}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {!isMyProfile && user && (
          <p className="text-xs text-gray-500 text-center mt-4">
            💡 לחץ על כפתור כדי להמליץ או להסיר המלצה
          </p>
        )}

        {!user && !isMyProfile && (
          <p className="text-xs text-gray-500 text-center mt-4">
            יש להיות מחובר כדי להמליץ
          </p>
        )}

        {endorsements.total === 0 && (
          <div className="text-center text-gray-500 py-4 mt-4">
            <ThumbsUp className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p>עדיין אין המלצות</p>
            {!isMyProfile && user && <p className="text-sm mt-2">היה הראשון להמליץ!</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PlayerEndorsements;
